<template>
  <el-card shadow="never" class="home-dashboard__card home-state-map">
    <div slot="header" class="home-state-map__header">
      <span class="home-dashboard__card-title" @click="toggle">{{ $t("postagens-por-estado") }}</span>
      <span class="home-state-map__controls">
        <PeriodSwitch v-if="open" v-model="days" :options="dayOptions" @change="fetch" />
        <span class="home-state-map__toggle" @click="toggle">
          {{ open ? $t("ocultar") : $t("exibir") }}
          <i :class="open ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
        </span>
      </span>
    </div>

    <div v-if="open" v-loading="loading" class="home-state-map__body" ref="body" @mouseleave="hover = null">
        <!-- Tooltip custom (o <title> nativo é feio e lento) -->
        <div v-if="hover" class="home-state-map__tooltip"
          :style="{ left: hover.x + 'px', top: hover.y + 'px', transform: hoverTransform }">
          <div class="home-state-map__tooltip-title">
            {{ hover.name }}
            <span v-if="hover.malha" class="home-state-map__tooltip-malha">Malha</span>
          </div>
          <div class="home-state-map__tooltip-total">
            {{ hover.total.toLocaleString("pt-BR") }} postagens · {{ hover.pct }}%
          </div>
          <div class="home-state-map__tooltip-row">
            <span class="home-state-map__legend-dot" style="background: #22418d"></span>
            +Expresso <b>{{ hover.expresso.toLocaleString("pt-BR") }}</b>
          </div>
          <div class="home-state-map__tooltip-row">
            <span class="home-state-map__legend-dot" style="background: #8f9dc0"></span>
            +Econômico <b>{{ hover.economico.toLocaleString("pt-BR") }}</b>
          </div>
          <div class="home-state-map__tooltip-row">
            <span class="home-state-map__legend-dot" style="background: #c0c4cc"></span>
            Outros <b>{{ hover.outros.toLocaleString("pt-BR") }}</b>
          </div>
        </div>
      <div class="home-state-map__map-col">
        <svg :viewBox="map.viewBox" class="home-state-map__svg" role="img">
          <path
            v-for="location in map.locations"
            :key="location.id"
            :d="location.path"
            class="home-state-map__path"
            :style="{ fill: fillFor(location.id) }"
            @mousemove="onHover(location, $event)"
            @mouseleave="hover = null"
            @click="goState(location.id.toUpperCase())"
          />
        </svg>
        <!-- Legenda: cores dos serviços + marcação da malha -->
        <div class="home-state-map__legend">
          <span class="home-state-map__legend-item">
            <span class="home-state-map__legend-dot" style="background: #f6921e"></span> Malha
          </span>
          <span class="home-state-map__legend-item">
            <span class="home-state-map__legend-dot" style="background: #22418d"></span> +Expresso
          </span>
          <span class="home-state-map__legend-item">
            <span class="home-state-map__legend-dot" style="background: #8f9dc0"></span> +Econômico
          </span>
          <span class="home-state-map__legend-item">
            <span class="home-state-map__legend-dot" style="background: #c0c4cc"></span> Outros
          </span>
        </div>
      </div>

      <div class="home-state-map__ranking">
        <div v-for="row in ranking" :key="row.state" class="home-state-map__rank-row"
          @mousemove="onHoverRow(row, $event)" @mouseleave="hover = null" @click="goState(row.state)">
          <span class="home-state-map__rank-uf" :class="{ 'home-state-map__rank-uf--malha': row.malha }">
            {{ row.state }}
          </span>
          <span class="home-state-map__rank-bar-track">
            <span class="home-state-map__rank-seg" style="background: #22418d" :style="{ width: row.wExpresso }"></span>
            <span class="home-state-map__rank-seg" style="background: #8f9dc0" :style="{ width: row.wEconomico }"></span>
            <span class="home-state-map__rank-seg" style="background: #c0c4cc" :style="{ width: row.wOutros }"></span>
          </span>
          <span class="home-state-map__rank-value">{{ row.total.toLocaleString("pt-BR") }}</span>
          <span class="home-state-map__rank-pct">{{ row.pct }}%</span>
        </div>
        <div v-if="!loading && ranking.length === 0" class="home-state-map__empty">
          {{ $t("sem-postagens") }}
        </div>
      </div>
    </div>
  </el-card>
</template>

<script>
import moment from "moment";
import "moment/locale/pt-br";
import { PrepostCountService } from "@/services/customers/prepost.count.services";
import BRAZIL_MAP from "./brazil-map";
import { MALHA_STATES } from "@/xpost/helpers/prepost-status";
import PeriodSwitch, { DAY_OPTIONS } from "./PeriodSwitch.vue";

export default {
  name: "HomeStateMap",
  components: { PeriodSwitch },
  data() {
    return {
      map: BRAZIL_MAP,
      open: true,
      loading: false,
      fetched: false,
      days: 30,
      dayOptions: DAY_OPTIONS,
      total: 0,
      states: [],
      hover: null,
    };
  },
  computed: {
    byState() {
      const index = {};
      this.states.forEach((row) => {
        index[row.state] = row;
      });
      return index;
    },
    hoverTransform() {
      if (!this.hover) return "";
      const x = this.hover.flipX ? "translateX(-100%)" : "";
      const y = this.hover.flipY ? "translateY(-100%)" : "";
      return `${x} ${y}`.trim();
    },
    maxCount() {
      return this.states.reduce((acc, row) => Math.max(acc, row.total), 0);
    },
    ranking() {
      const rows = this.states.slice();
      MALHA_STATES.forEach((uf) => {
        if (!this.byState[uf]) {
          rows.push({ state: uf, total: 0, expresso: 0, economico: 0, outros: 0 });
        }
      });
      const overall = this.total || 1;
      return rows
        .sort((a, b) => b.total - a.total || a.state.localeCompare(b.state))
        .map((row) => {
          const base = row.total || 1;
          return {
            ...row,
            malha: MALHA_STATES.includes(row.state),
            pct: ((row.total / overall) * 100).toFixed(1),
            wExpresso: `${(row.expresso / base) * 100}%`,
            wEconomico: `${(row.economico / base) * 100}%`,
            wOutros: `${(row.outros / base) * 100}%`,
          };
        });
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    // Clique no estado: abre a listagem com o MESMO recorte do gráfico
    // (status de postagem 3..17, período de criação hoje-N..hoje, UF destino)
    goState(uf) {
      this.$router.push({
        name: "post",
        query: {
          tab: "post",
          byCreationDate: "1",
          state: uf,
          startedAt: moment().subtract(this.days, "days").format("YYYY-MM-DD"),
          finishedAt: moment().format("YYYY-MM-DD"),
        },
      });
    },
    toggle() {
      this.open = !this.open;
      if (this.open && !this.fetched) {
        this.fetch();
      }
    },
    fetch() {
      const vm = this;
      vm.loading = true;
      PrepostCountService.state(vm.days)
        .then((response) => {
          vm.total = +response.total || 0;
          vm.states = response.states || [];
          vm.fetched = true;
        })
        .catch((error) => {
          vm.$message({ type: "error", message: error });
        })
        .finally(() => {
          vm.loading = false;
        });
    },
    countFor(id) {
      const row = this.byState[id.toUpperCase()];
      return row ? row.total : 0;
    },
    setHover(uf, name, event) {
      const row = this.byState[uf] || { total: 0, expresso: 0, economico: 0, outros: 0 };
      const rect = this.$refs.body.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const flipX = x > rect.width / 2;
      const flipY = y > rect.height / 2;
      this.hover = {
        name,
        malha: MALHA_STATES.includes(uf),
        total: row.total,
        expresso: row.expresso,
        economico: row.economico,
        outros: row.outros,
        pct: this.total ? ((row.total / this.total) * 100).toFixed(1) : "0.0",
        x: x + (flipX ? -14 : 14),
        y: y + (flipY ? -14 : 14),
        flipX,
        flipY,
      };
    },
    onHover(location, event) {
      this.setHover(location.id.toUpperCase(), location.name, event);
    },
    onHoverRow(row, event) {
      const location = this.map.locations.find(
        (item) => item.id.toUpperCase() === row.state
      );
      this.setHover(row.state, location ? location.name : row.state, event);
    },
    fillFor(id) {
      const uf = id.toUpperCase();
      const malha = MALHA_STATES.includes(uf);
      const value = this.countFor(uf);
      if (!value || !this.maxCount) {
        return malha ? "rgba(246, 146, 30, 0.18)" : "#eef1f6";
      }
      const level = Math.log(value + 1) / Math.log(this.maxCount + 1);
      return malha
        ? `rgba(246, 146, 30, ${0.3 + level * 0.7})`
        : `rgba(34, 65, 141, ${0.12 + level * 0.88})`;
    },
  },
};
</script>

<style>
.home-state-map__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.home-state-map__header .home-dashboard__card-title {
  cursor: pointer;
}

.home-state-map__controls {
  display: flex;
  align-items: center;
}

.home-state-map__toggle {
  font-size: 12px;
  color: var(--me-text-secondary);
  cursor: pointer;
  margin-left: 14px;
}

.home-state-map__body {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
}

.home-state-map__body {
  position: relative;
}

.home-state-map__map-col {
  flex: 0 1 340px;
  max-width: 340px;
  margin-right: 24px;
}

.home-state-map__tooltip {
  position: absolute;
  z-index: 10;
  pointer-events: none;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
  padding: 8px 12px;
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
}

.home-state-map__tooltip-title {
  font-weight: 700;
  color: #303133;
  margin-bottom: 2px;
}

.home-state-map__tooltip-malha {
  color: #f6921e;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-left: 4px;
}

.home-state-map__tooltip-total {
  color: #909399;
  margin-bottom: 6px;
  font-variant-numeric: tabular-nums;
}

.home-state-map__tooltip-row {
  display: flex;
  align-items: center;
  margin-bottom: 2px;
  font-variant-numeric: tabular-nums;
}

.home-state-map__tooltip-row b {
  margin-left: 5px;
  color: #303133;
}

.home-state-map__tooltip-row .home-state-map__legend-dot {
  margin-right: 6px;
}

.home-state-map__svg {
  width: 100%;
  height: auto;
}

.home-state-map__path {
  stroke: #fff;
  stroke-width: 1;
  transition: opacity 0.15s ease;
  cursor: pointer;
}

.home-state-map__path:hover {
  opacity: 0.75;
  stroke: var(--me-primary);
}

.home-state-map__legend {
  display: flex;
  flex-wrap: wrap;
  margin-top: 6px;
}

.home-state-map__legend-item {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  color: var(--me-text-secondary);
  margin-right: 14px;
}

.home-state-map__legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 5px;
}

.home-state-map__ranking {
  flex: 1 1 380px;
  min-width: 300px;
  column-count: 2;
  column-gap: 26px;
}

.home-state-map__rank-row {
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  font-size: 12px;
  break-inside: avoid;
}

.home-state-map__rank-uf {
  width: 26px;
  font-weight: 600;
  color: var(--me-text-regular);
}

.home-state-map__rank-uf--malha {
  color: #f6921e;
}

.home-state-map__rank-bar-track {
  flex: 1;
  display: flex;
  height: 6px;
  background: var(--me-fill);
  border-radius: 2px;
  margin: 0 8px;
  overflow: hidden;
}

.home-state-map__rank-seg {
  display: block;
  height: 100%;
  transition: width 0.4s ease;
}

.home-state-map__rank-value {
  min-width: 48px;
  text-align: right;
  color: var(--me-text-regular);
  font-variant-numeric: tabular-nums;
}

.home-state-map__rank-pct {
  min-width: 44px;
  text-align: right;
  color: var(--me-text-secondary);
  font-variant-numeric: tabular-nums;
}

.home-state-map__empty {
  color: var(--me-text-secondary);
  font-size: 13px;
  padding: 8px 0;
}

@media only screen and (max-width: 767px) {
  .home-state-map__map-col {
    margin-right: 0;
    margin-bottom: 14px;
  }

  .home-state-map__ranking {
    column-count: 1;
  }
}
</style>
